import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { GuardError, GuardStatus } from "./TenantWorkspaceGuard.styles";
import type {
  GuardOutcome,
  TenantWorkspaceGuardProps,
} from "./TenantWorkspaceGuard.types";
import { useTenantWorkspaces } from "../../hooks/useTenantContext";
import { useStoryWorkspace } from "../../context/StoryWorkspaceContext";
import { getApiErrorMessage } from "../../services/apiErrors";
import { clearStoredAuthSession } from "../../services/auth/persistLoginSession";
import {
  clearActiveWorkspace,
  getActiveTenantId,
  setActiveWorkspace,
  tryGetActiveWorkspaceId,
} from "../../services/workspace/activeWorkspace";

const getErrorStatus = (error: unknown): number | undefined =>
  typeof (error as { status?: unknown })?.status === "number"
    ? (error as { status: number }).status
    : undefined;

/**
 * Protects normal application routes using the tenant persisted at selection.
 * Only `tenantId`/`workspaceId` live in localStorage — the workspace list
 * itself always comes from `GET /tenants/:tenantId/workspaces`, so a hard
 * refresh (a fresh React Query cache) refetches it exactly once. Every other
 * consumer of that list (Header, Home, WorkspacesGrid) reads the same cache
 * entry via `useTenantWorkspaces`, so within one cache window it costs one
 * network request total, not one per component.
 */
const TenantWorkspaceGuard: React.FC<TenantWorkspaceGuardProps> = ({
  children,
}) => {
  const location = useLocation();
  const tenantId = getActiveTenantId();
  const { setWorkspaces, setWorkspace } = useStoryWorkspace();
  const workspacesQuery = useTenantWorkspaces(tenantId, Boolean(tenantId));

  const resolveOutcome = (): GuardOutcome => {
    if (!tenantId) return { kind: "selectTenant" };

    const errorStatus = getErrorStatus(workspacesQuery.error);
    if (errorStatus === 401) return { kind: "signedOut" };
    // 403 = platform caller, 404 = tenant no longer in the caller's memberships.
    if (errorStatus === 403 || errorStatus === 404) {
      return { kind: "selectTenant" };
    }
    if (workspacesQuery.isPending) return { kind: "loading" };
    if (workspacesQuery.isError) {
      return {
        kind: "error",
        message: getApiErrorMessage(
          workspacesQuery.error,
          "We could not load your workspace. Please try again.",
        ),
      };
    }
    if (!workspacesQuery.data.length) {
      return { kind: "selectTenant", noWorkspaces: true };
    }
    return { kind: "ready" };
  };

  const outcome = resolveOutcome();

  useEffect(() => {
    const workspaces = workspacesQuery.data;
    if (!workspaces?.length || !tenantId) return;

    setWorkspaces(
      workspaces.map((workspace) => ({
        id: workspace.id,
        name: workspace.name,
        description: workspace.description,
        tenant_id: workspace.tenant_id,
        is_active: workspace.is_active,
      })),
    );

    // Only fall back to the first workspace (backend sorts "Default" first) when
    // the persisted one is gone — otherwise a refresh would silently undo the
    // workspace the user picked in the workspace dropdown.
    const storedWorkspaceId = tryGetActiveWorkspaceId();
    const activeWorkspace =
      workspaces.find((workspace) => workspace.id === storedWorkspaceId) ??
      workspaces[0];
    if (activeWorkspace.id !== storedWorkspaceId) {
      setActiveWorkspace({ workspaceId: activeWorkspace.id, tenantId });
    }
    setWorkspace({
      id: activeWorkspace.id,
      name: activeWorkspace.name,
      description: activeWorkspace.description,
      tenant_id: activeWorkspace.tenant_id,
      is_active: activeWorkspace.is_active,
    });
  }, [tenantId, workspacesQuery.data, setWorkspaces, setWorkspace]);

  useEffect(() => {
    if (outcome.kind === "signedOut") clearStoredAuthSession();
    if (outcome.kind === "selectTenant") clearActiveWorkspace();
  }, [outcome.kind]);

  switch (outcome.kind) {
    case "signedOut":
      return <Navigate to="/login" replace />;
    case "selectTenant":
      return (
        <Navigate
          to="/select-organization"
          replace
          state={{ from: location, noWorkspaces: outcome.noWorkspaces }}
        />
      );
    case "error":
      return <GuardError role="alert">{outcome.message}</GuardError>;
    case "loading":
      return <GuardStatus role="status">Loading your workspace…</GuardStatus>;
    default:
      return <>{children}</>;
  }
};

export default TenantWorkspaceGuard;
